import { createBrowserRouter, Navigate, RouteObject, useNavigate, useParams, useSearchParams } from 'react-router';
import { ConsultantDashboard } from './components/ConsultantDashboard';
import { ClientDetailView } from './components/ClientDetailView';
import { SpaceDetailView } from './components/SpaceDetailView';
import { BillingAdminView } from './components/BillingAdminView';
import { ClientDashboardNew } from './components/ClientDashboardNew';
import { NotificationPreferencesPage } from './components/NotificationPreferencesPage';
import { MessagingPage } from './components/MessagingPage';
import { CELOnboardingPage } from './components/CELOnboardingPage';
import { RouteErrorPage } from './components/RouteErrorPage';

interface RouterConfig {
  accessToken: string;
  currentUser: any;
  onLogout: () => void;
}

import { WorkTypeDetail } from './components/WorkTypeDetail';
import { DashboardConfigView } from './components/DashboardConfigView';

// Wrapper components to provide navigation hooks
function ConsultantDashboardWrapper(props: any) {
  const navigate = useNavigate();
  // `/dashboard/:tab?` — the tab segment selects the dashboard tab; the same
  // route serves every tab so switching tabs never remounts the dashboard.
  const { tab } = useParams<{ tab?: string }>();
  
  return (
    <ConsultantDashboard
      {...props}
      tabSlug={tab ?? ''}
      onNavigate={(url: string) => navigate(url)}
      onViewClientDetails={(clientId: string) => navigate(`/clients/${clientId}`)}
      onViewSOWDetails={(sowId: string) => navigate(`/sows/${sowId}`)}
    />
  );
}

function ClientDetailViewWrapper(props: any) {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  
  if (!clientId) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <ClientDetailView
      {...props}
      companyId={clientId}
      onBack={() => navigate('/dashboard')}
      onNavigateToSpace={(companyId: string, spaceId: string) => {
        navigate(`/clients/${companyId}/spaces/${spaceId}`);
      }}
    />
  );
}

function SpaceDetailViewWrapper(props: any) {
  const { clientId, spaceId } = useParams<{ clientId: string; spaceId: string }>();
  const navigate = useNavigate();
  
  if (!clientId || !spaceId) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <SpaceDetailView
      {...props}
      companyId={clientId}
      spaceId={spaceId}
      onBack={() => navigate(`/clients/${clientId}`)}
    />
  );
}

function WorkTypeDetailWrapper(props: any) {
  return <WorkTypeDetail {...props} />;
}

function DashboardConfigViewWrapper(props: any) {
  const { clientId, sowId } = useParams<{ clientId: string; sowId: string }>();
  return <DashboardConfigView {...props} companyId={clientId!} sowId={sowId!} />;
}

import { QueueLibraryView } from './components/QueueLibraryView';

function QueueLibraryViewWrapper(props: any) {
  const { clientId, sowId } = useParams<{ clientId: string; sowId: string }>();
  return <QueueLibraryView {...props} companyId={clientId!} sowId={sowId!} />;
}

function MessagingPageWrapper(props: any) {
  const { engagementId } = useParams<{ engagementId: string }>();
  const navigate = useNavigate();
  // Read optional threadId from URL search params (hooks must run before any early return)
  const [searchParams] = useSearchParams();

  if (!engagementId) {
    return <Navigate to="/dashboard" replace />;
  }

  const threadId = searchParams.get('threadId') || undefined;

  return (
    <MessagingPage
      {...props}
      engagementId={engagementId}
      threadId={threadId}
      onBack={() => navigate('/dashboard')}
    />
  );
}

export function createAppRouter(config: RouterConfig) {
  const { accessToken, currentUser, onLogout } = config;
  
  // Helper to wrap components with common props
  const withAuth = (Component: any, additionalProps = {}) => {
    return (
      <Component
        accessToken={accessToken}
        currentUser={currentUser}
        onLogout={onLogout}
        {...additionalProps}
      />
    );
  };
  
  const routes: RouteObject[] = [
    {
      path: '/',
      // Every child route falls back to this on a render error or a thrown
      // response. Without it React Router shows its developer error page
      // (seen on /billing in the 2026-10-28 staging walk, finding S2).
      errorElement: <RouteErrorPage />,
      children: [
        // Default redirect to dashboard
        { 
          index: true, 
          element: <Navigate to="/dashboard" replace /> 
        },
        
        // Consultant Dashboard
        // The optional :tab segment gives each dashboard tab its own URL
        // (see utils/dashboardTabs.ts) while keeping one route, so the
        // dashboard stays mounted when the tab changes.
        { 
          path: 'dashboard/:tab?', 
          element: withAuth(ConsultantDashboardWrapper),
        },
        
        // Client Detail View
        { 
          path: 'clients/:clientId', 
          element: withAuth(ClientDetailViewWrapper),
        },
        
        // Space Detail View (nested under client)
        { 
          path: 'clients/:clientId/spaces/:spaceId', 
          element: withAuth(SpaceDetailViewWrapper),
        },
        
        // Work Type Detail View
        {
          path: 'work-types/:workTypeId',
          element: withAuth(WorkTypeDetailWrapper),
        },

        // Dashboard Domain Config
        {
          path: 'clients/:clientId/sow/:sowId/dashboards',
          element: withAuth(DashboardConfigViewWrapper),
        },

        // Queue Library (NEW)
        {
          path: 'clients/:clientId/sow/:sowId/queues',
          element: withAuth(QueueLibraryViewWrapper),
        },

        // Client Dashboard (for cfAdmin/cfUser roles)
        { 
          path: 'client-dashboard', 
          element: withAuth(ClientDashboardNew),
        },
        
        // The old all-tenants dashboard on a firm host is retired (RESTORE_PLAN R3.6): system admin tools live on
        // the platform host's System Admin panel, so a firm host's /admin goes to the firm's dashboard.
        { 
          path: 'admin', 
          element: <Navigate to="/dashboard" replace />,
        },
        
        // Billing. BillingAdminView loads the firm and renders the loading and
        // not-found states; the bare dashboard needs a firm and crashed here
        // with "Cannot read properties of undefined (reading 'subscriptionPlan')".
        { 
          path: 'billing', 
          element: withAuth(BillingAdminView),
        },
        
        // Notification Preferences
        { 
          path: 'notifications/preferences', 
          element: withAuth(NotificationPreferencesPage),
        },

        // Messaging Page
        {
          path: 'engagements/:engagementId/messages',
          element: withAuth(MessagingPageWrapper),
        },

        // Celo Onboarding Page
        {
          path: 'celo-onboarding',
          element: withAuth(CELOnboardingPage),
        },
        
        // Catch-all - redirect to dashboard
        { 
          path: '*', 
          element: <Navigate to="/dashboard" replace /> 
        },
      ],
    },
  ];
  
  return createBrowserRouter(routes);
}